import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { AppliedFilter, AppliedFiltersBar } from '@/components/DataTable/AppliedFiltersBar'
import { Filter, FilterControls } from '@/components/DataTable/FilterControls'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import useBrandFilter from '@/hooks/filters/useBrandFilter'
import useMarkingFilter from '@/hooks/filters/useMarkingFilter'
import useMaterialDistributionChannelFilter from '@/hooks/filters/useMaterialDistributionChannelFilter'
import useMaterialGroupFilter from '@/hooks/filters/useMaterialGroupFilter'
import useProductCategoryFilter from '@/hooks/filters/useProductCategoryFilter'
import useProductTypeFilter from '@/hooks/filters/useProductTypeFilter'
import useVariantFilter from '@/hooks/filters/useVariantFilter'
import {
  pnlExportQueryOptions,
  pnlMaterialsExportQueryOptions,
  pnlTableSearchSchema,
  useActualsPnl,
  useForecastsPnl,
} from '@/hooks/queries/crt/usePnl'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { getClearedFilters, resetFiltersPanel, useAppliedFilters } from '@/lib/utils/filters'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { parseISO } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfigureCrtPnl, type ConfigSelection } from '../-components/ConfigureCrtPnl'
import {
  buildKpiDashboardCsv,
  resolveKpiRows,
  toKpiDashboardCsvExport,
} from '../-components/kpi-dashboard-csv'
import { CrtVersionsFooter } from '../-components/CrtVersionsFooter'
import { getPnlView, type PnlView, toPnlViewSearch } from '../-components/pnl-view'
import { PnlViewSelector } from '../-components/PnlViewSelector'
import { useAppliedConfigBadges } from '../-components/useAppliedConfigBadges'
import { PROFITS_AND_LOSS_TABLE_SECTIONS } from './-components/profits-and-loss-table-config'
import { ProfitsAndLossSummaryTable } from './-components/ProfitsAndLossSummaryTable'
import { CrtVersionComputingNote } from '../-components/CrtVersionComputingNote'
import { PnlLastChanged } from '../-components/PnlLastChanged'

export const Route = createFileRoute('/_auth/_modules/crt/kpi-reports/profits-and-loss-summary/')({
  component: RouteComponent,
  validateSearch: (search) => pnlTableSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonth } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const queryClient = useQueryClient()

  const { crtPeriod, loadVersion, markSaved } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
  })

  const view = getPnlView(search)
  // Next year has no actuals yet: fetch forecast-only (`year=NY`) and skip the actuals query.
  const isNextYear = view === 'nextYear'
  // Actuals-only: every month actualised in the selected period's year, with no forecast — pick a December
  // period to read a closed year whole. The sandbox has nothing to configure.
  const isActualsOnly = view === 'actualsOnly'
  // The view is a URL param only, never the period: switching it keeps the period and the loaded version.
  const handleViewChange = (view: PnlView) => {
    navigate({ search: { ...search, ...toPnlViewSearch(view) } })
  }

  const { productCategoryFilter, productCategories, setProductCategories } =
    useProductCategoryFilter(search.productCategory)
  const { brandFilter, brands, setBrands } = useBrandFilter(search.brand)
  const { variantFilter, variants, setVariants } = useVariantFilter(search.variant)
  const { materialGroupFilter, materialGroups, setMaterialGroups } = useMaterialGroupFilter(
    search.materialGroupCode,
  )
  const { markingFilter, marking, setMarking } = useMarkingFilter(search.marking)
  const { productTypeFilter, productTypes, setProductTypes } = useProductTypeFilter(
    search.productType,
  )
  const { distributionChannelFilter, distributionChannels, setDistributionChannels } =
    useMaterialDistributionChannelFilter(search.distributionChannel)

  const filters = [
    productCategoryFilter,
    brandFilter,
    variantFilter,
    materialGroupFilter,
    markingFilter,
    productTypeFilter,
    distributionChannelFilter,
  ]

  const selectedFilters = {
    productCategory: productCategories?.length ? productCategories : undefined,
    brand: brands?.length ? brands : undefined,
    variant: variants?.length ? variants : undefined,
    materialGroupCode: materialGroups?.length ? materialGroups : undefined,
    marking: marking?.length ? marking : undefined,
    productType: productTypes?.length ? productTypes : undefined,
    distributionChannel: distributionChannels?.length ? distributionChannels : undefined,
  }

  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false)
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false)

  // Currency is display-only and picked in the header: it never reaches the backend, so it stays out of
  // `appliedFilters`. Called after the version sync so both can mirror into the URL on arrival.
  const { conversion } = useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true }),
  })

  // Every config control (units, assumptions) is staged in the config panel and committed
  // together via Apply. Commit them all in one navigate so none clobbers the others; empty selections drop
  // their param (baseline resolution). The loaded-version link is kept even when the assumption selection
  // changes, so re-saving still prefills the version's name; the query gating (see
  // `selectionMatchesVersion`) routes a diverged selection to a live compute instead of the now-stale
  // per-version cache.
  const handleApplyConfig = (config: ConfigSelection) => {
    navigate({
      search: {
        ...search,
        isPerMil: config.isPerMil || undefined,
        pricingAssumptionIds: config.pricingAssumptionIds.length
          ? config.pricingAssumptionIds
          : undefined,
        taxAssumptionIds: config.taxAssumptionIds.length ? config.taxAssumptionIds : undefined,
        manualInvestmentIds: config.manualInvestmentIds.length
          ? config.manualInvestmentIds
          : undefined,
        volumeScenarioId: config.volumeScenarioId ?? undefined,
      },
    })
  }

  const appliedFilterBadges = useAppliedFilters(filters as Filter<unknown>[], search)
  // The sandbox stays in the URL through actuals-only so switching back restores it, but it applies to
  // neither the request nor the badges there.
  const sandbox = isActualsOnly
    ? {}
    : {
        pricingAssumptionIds: search.pricingAssumptionIds,
        taxAssumptionIds: search.taxAssumptionIds,
        manualInvestmentIds: search.manualInvestmentIds,
        // Forward the loaded version so the backend can serve its precomputed rows; the handler drops back to
        // a live compute when the selection has diverged from the version.
        loadedVersionId: search.loadedVersionId,
        volumeScenarioId: search.volumeScenarioId,
      }

  const appliedConfigBadges = useAppliedConfigBadges({
    isPerMil: !!search.isPerMil,
    pricingAssumptionIds: sandbox.pricingAssumptionIds,
    taxAssumptionIds: sandbox.taxAssumptionIds,
    manualInvestmentIds: sandbox.manualInvestmentIds,
    volumeScenarioId: sandbox.volumeScenarioId,
  })

  // Filters and configs share one bar; each badge opens the panel it came from.
  const handleBadgeClick = (badge: AppliedFilter) => {
    if (appliedConfigBadges.some((config) => config.key === badge.key)) {
      setIsConfigPanelOpen(true)
    } else {
      setIsFiltersPanelOpen(true)
    }
  }

  // Re-sync the panel's local state to the applied URL params whenever it opens, so an aborted edit
  // (open → change → close without applying) does not linger on the next open.
  const handleFiltersPanelOpenChange = () => {
    setIsFiltersPanelOpen((open) => {
      if (!open) resetFiltersPanel(filters as Filter<unknown>[], search)
      return !open
    })
  }

  const showResults = () => {
    navigate({ search: { ...search, ...selectedFilters } })
  }

  const resetAllFilters = () => {
    setProductCategories([])
    setBrands([])
    setVariants([])
    setMaterialGroups([])
    setMarking([])
    setProductTypes([])
    setDistributionChannels([])
    navigate({ search: { ...search, ...getClearedFilters(selectedFilters) } })
  }

  // Fetch a BPC feed export; when the backend flagged any over-1M per-unit value, return a warning so the
  // menu shows it instead of the success toast. The flagged SKUs carry an `error` column in the CSV.
  const exportBpcFeed = async (distributionChannel: 'domestic' | 'export') => {
    const { blob, filename, invalidCount } = await queryClient.ensureQueryData(
      pnlExportQueryOptions(crtPeriod, distributionChannel),
    )
    return {
      blob,
      filename,
      warning:
        invalidCount > 0
          ? {
              title: t('kpiReports.profitsAndLossSummary.export.invalidValuesTitle'),
              description: t('kpiReports.profitsAndLossSummary.export.invalidValuesMessage', {
                count: invalidCount,
              }),
            }
          : undefined,
    }
  }

  const appliedFilters = {
    productCategory: search.productCategory,
    brand: search.brand,
    variant: search.variant,
    materialGroupCode: search.materialGroupCode,
    marking: search.marking,
    productType: search.productType,
    distributionChannel: search.distributionChannel,
    isPerMil: search.isPerMil,
    year: search.year,
    // Without the sandbox, actuals-only reads its precomputed full-year slice rather than a live compute.
    actualsOnly: isActualsOnly || undefined,
    ...sandbox,
  }

  const {
    data: actualsData,
    isPending: isActualsPending,
    error: actualsError,
  } = useActualsPnl(crtPeriod, appliedFilters, { enabled: !isNextYear })
  const {
    data: forecastsData,
    isPending: isForecastsPending,
    error: forecastsError,
  } = useForecastsPnl(crtPeriod, appliedFilters, { enabled: !isActualsOnly })

  // Keep the skeletons until BOTH queries resolve — otherwise the faster one clears them while the
  // other's cells are still empty. In next-year mode the actuals query is disabled (stays `pending`
  // forever), and in actuals-only mode the forecasts query is, so ignore the disabled one.
  const isPending = (!isActualsOnly && isForecastsPending) || (!isNextYear && isActualsPending)
  const error = isNextYear ? forecastsError : actualsError
  const months = (isNextYear ? forecastsData : actualsData)?.meta.months ?? []

  // The table as displayed, built from the same loaded trees the table reads.
  const dashboardRows = resolveKpiRows(
    PROFITS_AND_LOSS_TABLE_SECTIONS,
    isNextYear ? undefined : actualsData?.breakdown,
    isActualsOnly ? undefined : forecastsData?.breakdown,
  )
  const exportDashboard = () =>
    Promise.resolve(
      toKpiDashboardCsvExport(
        buildKpiDashboardCsv({
          rows: dashboardRows,
          monthLabels: months.map((month) => formatMonth(parseISO(month), { month: 'short' })),
          labelOf: (key) => t(`kpis.labels.${key}`),
          headers: {
            description: t('kpiReports.columns.description'),
            fullYearTotal: t('kpiReports.columns.fullYearTotal'),
          },
          currency: conversion,
        }),
        'profits-and-loss-summary',
      ),
    )

  return (
    <PageContainer maxWidth="wide" className="pb-28">
      <Spread>
        <PageHeader>{t('kpiReports.profitsAndLossSummary.pageHeader')}</PageHeader>
        <VStack gap="md" align="end">
          <HStack gap="sm">
            <PnlViewSelector value={view} onChange={handleViewChange} />
            <FilterControls
              filters={filters as Filter<unknown>[]}
              activeFilterCount={appliedFilterBadges.length}
              isOpen={isFiltersPanelOpen}
              onOpenChange={handleFiltersPanelOpenChange}
              onShowResults={showResults}
              onResetAllFilters={resetAllFilters}
            />
            <ConfigureCrtPnl
              isOpen={isConfigPanelOpen}
              onOpenChange={() => setIsConfigPanelOpen((open) => !open)}
              isPerMil={!!search.isPerMil}
              crtPeriod={crtPeriod}
              isActualsOnly={isActualsOnly}
              pricingAssumptionIds={search.pricingAssumptionIds}
              taxAssumptionIds={search.taxAssumptionIds}
              manualInvestmentIds={search.manualInvestmentIds}
              volumeScenarioId={search.volumeScenarioId ?? null}
              onApply={handleApplyConfig}
            />
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('kpiReports.export.dashboard')}
                  description={t('kpiReports.export.dashboardDescription')}
                  getCsvExport={exportDashboard}
                  rowCount={isPending ? 0 : dashboardRows.length}
                />,
                // The SKU exports report the year-to-date + forecast build; the actuals-only view has no such file.
                ...(isActualsOnly
                  ? []
                  : [
                      <ExportCsvMenuItem
                        label={t('export.viewData', { ns: 'common' })}
                        description={t(
                          'kpiReports.profitsAndLossSummary.export.materialsDescription',
                        )}
                        getCsvExport={() =>
                          queryClient.ensureQueryData(
                            // Mirror what the table sends so the CSV matches the on-screen view: the raw
                            // assumption ids fully describe it (including a layered selection).
                            pnlMaterialsExportQueryOptions(crtPeriod, {
                              pricingAssumptionIds: search.pricingAssumptionIds,
                              taxAssumptionIds: search.taxAssumptionIds,
                              manualInvestmentIds: search.manualInvestmentIds,
                            }),
                          )
                        }
                        rowCount={1}
                        canExceedRowCountLimit
                      />,
                      <ExportCsvMenuItem
                        label={t('kpiReports.profitsAndLossSummary.export.domestic')}
                        description={t(
                          'kpiReports.profitsAndLossSummary.export.domesticDescription',
                        )}
                        getCsvExport={() => exportBpcFeed('domestic')}
                        rowCount={1}
                        canExceedRowCountLimit
                      />,
                      <ExportCsvMenuItem
                        label={t('kpiReports.profitsAndLossSummary.export.cdf')}
                        description={t('kpiReports.profitsAndLossSummary.export.cdfDescription')}
                        getCsvExport={() => exportBpcFeed('export')}
                        rowCount={1}
                        canExceedRowCountLimit
                      />,
                    ]),
              ]}
            />
          </HStack>
        </VStack>
      </Spread>
      <CrtVersionComputingNote crtPeriod={crtPeriod} versionId={search.loadedVersionId} />

      <Card className="relative w-full">
        <PnlLastChanged
          lastChangedAts={[
            isNextYear ? undefined : actualsData?.meta.lastChangedAt,
            isActualsOnly ? undefined : forecastsData?.meta.lastChangedAt,
          ]}
        />
        <CardContent className="pt-6">
          {/* Same card as the table so the bar reads on white, as on the detail tables. */}
          <VStack gap="lg" align="stretch">
            <AppliedFiltersBar
              label={t('assumptionsConfig.appliedLabel')}
              appliedFilters={[...appliedFilterBadges, ...appliedConfigBadges]}
              onBadgeClick={handleBadgeClick}
            />
            <ProfitsAndLossSummaryTable
              sections={PROFITS_AND_LOSS_TABLE_SECTIONS}
              actuals={isNextYear ? undefined : actualsData?.breakdown}
              forecast={isActualsOnly ? undefined : forecastsData?.breakdown}
              months={months}
              crtPeriod={crtPeriod}
              isNextYear={isNextYear}
              isActualsOnly={isActualsOnly}
              isPending={isPending}
              error={error}
              currency={conversion}
              isPerMil={!!search.isPerMil}
            />
          </VStack>
        </CardContent>
      </Card>

      <CrtVersionsFooter
        crtPeriod={crtPeriod}
        pricingAssumptionIds={search.pricingAssumptionIds}
        taxAssumptionIds={search.taxAssumptionIds}
        manualInvestmentIds={search.manualInvestmentIds}
        volumeScenarioId={search.volumeScenarioId}
        loadedVersionId={search.loadedVersionId}
        onLoadVersion={loadVersion}
        onSaved={markSaved}
      />
    </PageContainer>
  )
}
